import { useI18n } from '../lib/i18n';
import { pibError, mbError, digitsOnly } from '../lib/validation';
import ImportFromImage from './ImportFromImage';
import CompanyLookup from './CompanyLookup';

// ============================================================
// Polja klijenta — ista u formi klijenta i kod "+ Novi klijent" u fakturi.
// Redosled: tip (Domaći / Strani) · Fizičko lice · PIB (+ automatsko preuzimanje) · naziv · MB · adresa · kontakt
// form: { name, pib, mb, address, city, postal_code, country, email, phone, is_foreign, is_individual }
// ============================================================
export const EMPTY_CLIENT = {
  name: '', pib: '', mb: '', address: '', city: '', postal_code: '',
  country: 'Srbija', email: '', phone: '', is_foreign: false, is_individual: false,
};

// Provera pre čuvanja; vraća tekst greške ili ''
export function clientError(form, tr) {
  if (!String(form.name || '').trim()) return tr('Upišite naziv klijenta.', 'Enter the client name.', 'Введите название клиента.');
  if (!form.is_foreign && !form.is_individual) return pibError(form.pib) || mbError(form.mb);
  return '';
}

export default function ClientFields({ form, setForm, touched = {}, setTouched = () => {}, compact = false }) {
  const { t, tr } = useI18n();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const domesticCompany = !form.is_foreign && !form.is_individual;
  const setId = (k, max) => (e) => {
    const v = form.is_foreign ? e.target.value : digitsOnly(e.target.value).slice(0, max);
    setForm((f) => ({ ...f, [k]: v }));
  };
  const pibErr = domesticCompany ? pibError(form.pib) : '';
  const mbErr = domesticCompany ? mbError(form.mb) : '';
  const showPibErr = (touched.pib || digitsOnly(form.pib).length === 9) && pibErr;
  const showMbErr = (touched.mb || digitsOnly(form.mb).length === 8) && mbErr;

  const setType = (foreign) => setForm((f) => ({
    ...f, is_foreign: foreign,
    country: foreign ? (f.country === 'Srbija' ? '' : f.country) : 'Srbija',
  }));

  const fill = (c) => {
    setForm((f) => ({
      ...f,
      name: c.name || f.name,
      mb: c.mb ? digitsOnly(c.mb) : f.mb,
      address: c.address || f.address,
      city: c.city || f.city,
      postal_code: c.postal_code || f.postal_code,
    }));
    setTouched({ pib: true, mb: true });
  };

  const st = compact ? cs : s;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* Tip klijenta */}
      <span style={st.label}>{tr('Izaberite tip klijenta', 'Choose client type', 'Тип клиента')}</span>
      <div style={st.typeRow} role="group">
        {[[false, tr('Domaći', 'Domestic', 'Местный')], [true, tr('Strani', 'Foreign', 'Иностранный')]].map(([v, l]) => (
          <button key={l} type="button" onClick={() => setType(v)} aria-pressed={form.is_foreign === v}
            style={{ ...st.typeBtn, ...(form.is_foreign === v ? st.typeOn : {}) }}>{l}</button>
        ))}
      </div>
      <label style={st.checkRow}>
        <input type="checkbox" checked={!!form.is_individual} onChange={(e) => setForm((f) => ({ ...f, is_individual: e.target.checked }))} />
        <span>{tr('Fizičko lice', 'Private individual', 'Физическое лицо')}</span>
      </label>

      {/* Strani klijenti: "Popuni sa slike" (za domaće podatke daje PIB) */}
      {form.is_foreign && <ImportFromImage onData={(d) => setForm((f) => ({ ...f, ...d, is_foreign: true }))} />}

      {/* PIB prvo — za domaće firme podaci se preuzimaju automatski */}
      {!form.is_individual && (
        <>
          <span style={st.label}>{form.is_foreign ? 'Tax ID' : <>{t('pibLabel')} <span style={{ color: 'var(--red-600)' }}>*</span></>}</span>
          <input
            value={form.pib}
            onChange={setId('pib', 9)}
            onBlur={() => setTouched((x) => ({ ...x, pib: true }))}
            inputMode={form.is_foreign ? 'text' : 'numeric'}
            placeholder={form.is_foreign ? '' : tr('Unesite PIB klijenta (9 cifara)', 'Enter the client tax ID (9 digits)', 'Введите ИНН (9 цифр)')}
            style={{ ...st.input, ...(showPibErr ? st.inputBad : {}) }}
          />
          {showPibErr && <div style={st.fieldError}>{pibErr}</div>}
          {domesticCompany && <CompanyLookup pib={form.pib} onFound={fill} />}
        </>
      )}

      <span style={st.label}>{form.is_individual ? tr('Ime i prezime', 'Full name', 'Имя и фамилия') : tr('Naziv klijenta', 'Client name', 'Название клиента')} <span style={{ color: 'var(--red-600)' }}>*</span></span>
      <input value={form.name} onChange={set('name')} style={st.input} />

      {!form.is_individual && (
        <>
          <span style={st.label}>{form.is_foreign ? 'Company registration number' : t('mbLabel')}</span>
          <input
            value={form.mb}
            onChange={setId('mb', 8)}
            onBlur={() => setTouched((x) => ({ ...x, mb: true }))}
            inputMode={form.is_foreign ? 'text' : 'numeric'}
            placeholder={form.is_foreign ? '' : tr('8 cifara', '8 digits', '8 цифр')}
            style={{ ...st.input, ...(showMbErr ? st.inputBad : {}) }}
          />
          {showMbErr && <div style={st.fieldError}>{mbErr}</div>}
        </>
      )}

      <span style={st.label}>{t('addressPlaceholder')}</span>
      <input value={form.address} onChange={set('address')} style={st.input} />
      <div style={st.grid2}>
        <div><span style={st.label}>{t('cityLabel')}</span><input value={form.city} onChange={set('city')} style={st.input} /></div>
        <div><span style={st.label}>{t('postalCodeLabel')}</span><input value={form.postal_code} onChange={set('postal_code')} style={st.input} /></div>
      </div>
      {form.is_foreign && (
        <><span style={st.label}>{t('countryLabel')}</span><input value={form.country} onChange={set('country')} style={st.input} /></>
      )}
      <div style={st.grid2}>
        <div><span style={st.label}>{t('emailLabel')}</span><input type="email" value={form.email} onChange={set('email')} style={st.input} /></div>
        <div><span style={st.label}>{t('phoneLabel')}</span><input value={form.phone} onChange={set('phone')} style={st.input} /></div>
      </div>
    </div>
  );
}

const s = {
  label: { fontSize: 13, fontWeight: 600, color: 'var(--ink-700)', marginBottom: 5, display: 'block' },
  input: { border: '1px solid var(--line)', borderRadius: 8, padding: '0 12px', height: 40, fontSize: 14, width: '100%', marginBottom: 12, background: 'var(--surface)' },
  inputBad: { borderColor: 'var(--red-600)', background: 'var(--red-100)' },
  fieldError: { fontSize: 12, color: 'var(--red-600)', marginTop: -8, marginBottom: 10 },
  grid2: { display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 12 },
  typeRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 },
  typeBtn: { height: 40, border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', color: 'var(--ink-900)', fontSize: 13, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer' },
  typeOn: { background: 'var(--teal-800)', borderColor: 'var(--teal-800)', color: '#fff' },
  checkRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--ink-700)', margin: '0 0 16px' },
};
// Malo zbijenije u fakturi
const cs = { ...s, input: { ...s.input, height: 38, marginBottom: 10 }, label: { ...s.label, fontSize: 12.5, marginBottom: 4 }, checkRow: { ...s.checkRow, margin: '0 0 12px' } };
